<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { Editor, rootCtx, defaultValueCtx, editorViewCtx } from '@milkdown/kit/core';
  import { commonmark } from '@milkdown/kit/preset/commonmark';
  import { gfm } from '@milkdown/kit/preset/gfm';
  import { listener, listenerCtx } from '@milkdown/kit/plugin/listener';
  import { history } from '@milkdown/kit/plugin/history';
  import { clipboard } from '@milkdown/kit/plugin/clipboard';
  import { trailing } from '@milkdown/kit/plugin/trailing';
  import { indent } from '@milkdown/kit/plugin/indent';

  // Import Preparo theme (combines Nord colors + Pandoc styles + Preparo customizations)
  import { preparo } from '@preparo/theme-preparo';

  // Import Pandoc Essentials preset for extended Markdown support
  import { pandocEssentials, preprocessPandocDivs, postprocessPandocDivs } from '@preparo/preset-pandoc-essentials';

  // Import custom plugins
  import { footnotePlugin } from '../../plugins/footnotePlugin';
  import { selectionPlugin } from '../../plugins/selectionPlugin';
  import { figureImagePlugin } from '../../plugins/figureImagePlugin';
  import { headingBadgePlugin } from '../../plugins/headingBadgePlugin';
  import { placeholderPlugin } from '../../plugins/placeholderPlugin';
  import { citationPlugin } from '../../plugins/citationPlugin';
  import { epubTypePlugin, setDocumentType } from '../../plugins/epubTypePlugin';

  // Import stores
  import { editorContent, editorInstance, editorActions, currentTemplateId, yamlFrontmatter as yamlFrontmatterStore } from '../../stores/editorStore';
  import { get } from 'svelte/store';

  // Import config
  import { editor as editorConfig } from '../../config';

  // Import types
  import type { DocumentType } from '../../types';

  // Component props
  export let docId: string | null = null;
  export let initialContent: string = '';
  export let placeholder: string = 'Start writing your notes...';
  export let readonly: boolean = false;
  export let documentType: DocumentType | null = null;

  // Local variables
  let editorRef: HTMLDivElement;
  let editor: Editor;

  // YAML frontmatter is managed via the yamlFrontmatterStore (shared with NotesPanel).
  // Read with get() for non-reactive access in callbacks.
  function getFrontmatter(): string { return get(yamlFrontmatterStore); }

  // Instance management for safe cleanup
  // Each component instance gets a unique ID to prevent orphan saves
  const instanceId: string = crypto.randomUUID();
  let isDestroyed: boolean = false;

  // Debounced save function with instance safety check
  let saveTimeout: ReturnType<typeof setTimeout>;
  function debouncedSave(content: string): void {
    clearTimeout(saveTimeout);

    // Capture current instance state
    const currentInstanceId = instanceId;
    const currentDocId = docId;

    saveTimeout = setTimeout(() => {
      // Safety check: only save if this instance is still active
      // This prevents orphan saves when component is rapidly recreated via {#key}
      if (isDestroyed || currentInstanceId !== instanceId) {
        console.log('⚠️ Skipping orphan save (instance destroyed or changed)');
        return;
      }

      if (currentDocId) {
        // Postprocess to restore Pandoc syntax + YAML frontmatter before saving
        const restoredContent = postprocessPandocDivs(content);
        const fm = getFrontmatter();
        const pandocContent = fm ? fm + '\n' + restoredContent : restoredContent;
        // Save to template or document depending on current mode
        if (get(currentTemplateId)) {
          editorActions.saveTemplate(pandocContent);
        } else {
          editorActions.saveDoc(currentDocId, pandocContent);
        }
      }
    }, editorConfig.autoSaveDelay);
  }

  onMount(async () => {
    // Set document type for epub:type enforcement
    setDocumentType(documentType);

    // Extract YAML frontmatter (--- ... --- or --- ... ...) before editor sees it
    let contentWithoutFrontmatter = initialContent;
    const frontmatterMatch = initialContent.match(/^---\n([\s\S]*?\n)(?:---|\.\.\.)\n?/);
    if (frontmatterMatch) {
      let fm = frontmatterMatch[0];
      // Ensure frontmatter ends with a newline for clean restoration
      if (!fm.endsWith('\n')) {
        fm += '\n';
      }
      yamlFrontmatterStore.set(fm);
      contentWithoutFrontmatter = initialContent.slice(frontmatterMatch[0].length).replace(/^\n/, '');
      console.log('📋 YAML frontmatter extracted (' + fm.split('\n').length + ' lines)');
    } else {
      yamlFrontmatterStore.set('');
    }

    // Preprocess content to transform Pandoc fenced divs to directive syntax
    const processedContent = preprocessPandocDivs(contentWithoutFrontmatter);
    console.log('📝 Preprocessing fenced divs:');
    console.log('  Original:', contentWithoutFrontmatter.substring(0, 200));
    console.log('  Processed:', processedContent.substring(0, 200));

    try {
      editor = await Editor.make()
        .config((ctx) => {
          // Root DOM configuration
          ctx.set(rootCtx, editorRef);

          // Initial value configuration (preprocessed for directive syntax)
          ctx.set(defaultValueCtx, processedContent);

          // Listener configuration for auto-save
          ctx.get(listenerCtx).markdownUpdated((ctx, markdown) => {
            // Postprocess to Pandoc syntax for external consumers
            const restoredMarkdown = postprocessPandocDivs(markdown);
            const fm = getFrontmatter();
            const pandocMarkdown = fm ? fm + '\n' + restoredMarkdown : restoredMarkdown;
            editorContent.set(pandocMarkdown);
            debouncedSave(markdown);
          });
        })
        .config(preparo) // Preparo theme
        .use(commonmark) // Basic Markdown support
        .use(gfm) // GitHub Flavored Markdown (tables, strikethrough, task lists)
        .use(pandocEssentials) // Pandoc extensions (superscript, spans, divs, heading attrs)
        .use(figureImagePlugin) // Custom image rendering with figure/figcaption
        .use(headingBadgePlugin) // Heading attributes as visual badges
        .use(placeholderPlugin) // {{PLACEHOLDER}} → visual badges with metadata values
        .use(citationPlugin) // [@key] → formatted citation badges
        .use(listener) // Event listeners
        .use(history) // Undo/redo
        .use(clipboard) // Enhanced copy/paste
        .use(trailing) // Always keep a paragraph at the end
        .use(indent) // Indentation support
        .use(footnotePlugin) // Footnote plugin with auto-numbering
        .use(selectionPlugin) // Selection tracking plugin (replaces polling)
        .use(epubTypePlugin) // Enforce epub:type on H1 for frontmatter/backmatter
        .create();

      // Apply readonly mode if needed
      if (readonly) {
        editor.action((ctx) => {
          const view = ctx.get(editorViewCtx);
          view.setProps({ editable: () => false });
        });
      }

      // Save editor instance to store
      editorInstance.set(editor);

      console.log('✅ Milkdown editor initialized', readonly ? '(readonly)' : '');
    } catch (error) {
      console.error('❌ Failed to initialize Milkdown editor:', error);
    }
  });

  onDestroy(() => {
    // Mark instance as destroyed FIRST to prevent any pending saves
    isDestroyed = true;

    // Clear any pending timeout
    clearTimeout(saveTimeout);

    // Destroy the editor instance
    if (editor) {
      editor.destroy();
      console.log('🧹 Milkdown editor destroyed');
    }
  });

  // Note: No reactive logic to update content!
  // The component is recreated via {#key} in NotesPanel when the document changes.
  // This avoids the reactivity loop that caused the cursor bug.

  /**
   * Handle dragover to allow dropping images from the panel
   */
  function handleDragOver(event: DragEvent): void {
    if (event.dataTransfer?.types.includes('application/x-preparo-image')) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  /**
   * Handle drop to insert image from panel
   */
  function handleDrop(event: DragEvent): void {
    // Check if this is a Preparo image drop
    if (!event.dataTransfer?.types.includes('application/x-preparo-image')) {
      return; // Let other drops be handled normally
    }

    // Prevent default IMMEDIATELY to stop browser/ProseMirror from handling it
    event.preventDefault();
    event.stopPropagation();

    const imageData = event.dataTransfer.getData('application/x-preparo-image');
    if (!imageData || !editor) return;

    try {
      const { filename, altText } = JSON.parse(imageData);

      // Insert image node using ProseMirror
      editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        const { state, dispatch } = view;
        const { schema } = state;

        // Get drop position from mouse coordinates
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
        if (!pos) return;

        // Create image node
        const imageType = schema.nodes.image;
        if (!imageType) return;

        const imageNode = imageType.create({ src: filename, alt: altText, title: altText });
        const tr = state.tr.insert(pos.pos, imageNode);
        dispatch(tr);
      });
    } catch (error) {
      console.error('Failed to drop image:', error);
    }
  }
</script>

<div
  class="milkdown-wrapper"
  class:readonly
  on:dragover|capture={handleDragOver}
  on:drop|capture={handleDrop}
>
  <div
    bind:this={editorRef}
    class="milkdown-editor"
    data-placeholder={placeholder}
  />
</div>

<style>
  .milkdown-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--panel-background, #fff);
    border-radius: 4px;
  }

  .milkdown-wrapper.readonly {
    pointer-events: none;
    opacity: 0.7;
  }

  .milkdown-editor {
    flex: 1;
    overflow-y: auto;
  }

  /* Ensure milkdown fills the container */
  :global(.milkdown) {
    height: 100%;
  }
</style>
